import { homeFeedAdPreviewHtml } from "../ads.ts";
import { formatDistance, t } from "../i18n/index.ts";
import { escapeHtml } from "../html.ts";
import { observeRouteThumbnails, QUEST_LIST_THUMB_PLACEHOLDER, setRouteThumbnailLookup, type RouteThumbnailRequest } from "../quest/route-thumbnail-pool.ts";
import {
  hasListRoutePreview,
  isSpotOnlyListQuest,
  listThumbnailUsesSpotMarkers,
  questListDistanceM,
  questListRouteLengthM,
  spotCoordinates,
} from "../../shared/quest-feed.ts";
import { walkLabel, formatMinutes } from "../quest/summary.ts";
import type { NearbyQuest, QuestDetail } from "../quest/types.ts";
import type { NearbyViewerAt } from "./nearby-viewer.ts";

/**
 * Home left-panel lists (Nearby, My quests, Saved): tabs, card markup, thumbnails, the per-card
 * zoom button, and click routing. A card click opens cache-first full detail or a light preview; zoom fits the route.
 */


function spotCountLabel(count: number): string {
  return t(count === 1 ? "rt.page.spotCount.one" : "rt.page.spotCount", { count });
}

export { spotCountLabel };

const HOME_TAB_KEY = "mapus.homeTab";
const QUEST_ZOOM_ICON =
  '<svg class="nearby-item-zoom-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="2"/><path d="M14.5 14.5L20 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const CARD_SELECTOR = "button.nearby-item[data-id], button.piece-item[data-quest-id], button.nearby-item[data-quest-id]";

const thumbSources = new Map<string, RouteThumbnailRequest>();

export function questTitle(quest: { title?: string | null }): string {
  return (quest.title ?? "").trim() || t("quests.untitled");
}

/** Remember the preview route or spot path a thumbnail `<img data-thumb-id>` should render. */
export function rememberThumbSource(
  item: {
    id: string;
    quest_type?: string | null;
    route_preview?: NearbyQuest["route_preview"];
    spots_preview?: NearbyQuest["spots_preview"];
    spot_count?: number | null;
  },
): void {
  if (listThumbnailUsesSpotMarkers(item)) {
    const spots = item.spots_preview ?? [];
    if (spotCoordinates(spots).length >= 1) {
      thumbSources.set(item.id, {
        id: item.id,
        mode: "spot",
        spots,
      });
      return;
    }
  }
  if (hasListRoutePreview(item)) {
    const preview = item.route_preview!;
    thumbSources.set(item.id, {
      id: item.id,
      mode: "route",
      coordinates: preview.coordinates,
      canvas: preview.canvas ?? null,
      paint: preview.paint ?? null,
      fill: preview.fill ?? null,
    });
    return;
  }
  thumbSources.set(item.id, { id: item.id, mode: "placeholder" });
}

function questListZoomButton(id: string): string {
  return `<button type="button" class="nearby-item-zoom" data-quest-zoom="${escapeHtml(id)}" aria-label="${escapeHtml(t("home.questZoom"))}">${QUEST_ZOOM_ICON}</button>`;
}

export function nearbyItemHtml(
  item: {
    id: string;
    title: string;
    quest_type?: string | null;
    route_preview?: NearbyQuest["route_preview"];
    spots_preview?: NearbyQuest["spots_preview"];
    spot_count?: number;
  },
  current: boolean,
  metaLine: string,
  extra: { className?: string; attrs?: string; metaHtml?: boolean } = {},
): string {
  rememberThumbSource(item);
  const classes = ["nearby-item", extra.className].filter(Boolean).join(" ");
  const attrs = extra.attrs ?? `data-id="${escapeHtml(item.id)}"`;
  const meta = extra.metaHtml ? metaLine : escapeHtml(metaLine);
  return `<div class="nearby-item-shell">
    <button type="button" class="${classes}" ${attrs} aria-current="${current ? "true" : "false"}">
      <span class="nearby-item-row">
        <img class="nearby-item-thumb is-placeholder" alt="" loading="lazy" decoding="async" src="${QUEST_LIST_THUMB_PLACEHOLDER}" data-thumb-id="${escapeHtml(item.id)}" />
        <span class="nearby-item-body">
          <strong>${escapeHtml(item.title)}</strong>
          <span>${meta}</span>
        </span>
      </span>
    </button>
    ${questListZoomButton(item.id)}
  </div>`;
}

/** "N km away · length · time" under a card title (also the preview panel meta). */
export function questListMetaLine(
  item: {
    distance_m: number;
    route_preview?: NearbyQuest["route_preview"];
    start?: NearbyQuest["start"];
    spot_count?: number;
    est_minutes_total?: number;
    spots_preview?: NearbyQuest["spots_preview"];
  },
  viewer: NearbyViewerAt | null,
): string {
  if (isSpotOnlyListQuest(item)) {
    const count = spotCountLabel(item.spot_count ?? item.spots_preview?.length ?? 0);
    const minutes = item.est_minutes_total ?? 0;
    const time = formatMinutes(minutes);
    const awayM = questListDistanceM(item, viewer);
    if (awayM == null) return t("nearby.metaSpotsRoute", { count, time });
    return t("nearby.metaSpots", { away: formatDistance(awayM), count, time });
  }
  const routeLen = questListRouteLengthM(item);
  const length = formatDistance(routeLen);
  const time = walkLabel(routeLen).slice(1);
  const awayM = questListDistanceM(item, viewer);
  if (awayM == null) {
    return t("nearby.metaRoute", { length, time });
  }
  return t("nearby.meta", {
    away: formatDistance(awayM),
    length,
    time,
  });
}

function pushListCard(parts: string[], index: number, html: string, listAdPreview: boolean): void {
  parts.push(html);
  if (listAdPreview && index === 4) {
    const feed = homeFeedAdPreviewHtml();
    if (feed) parts.push(feed);
  }
}

function renderCards(
  root: HTMLElement,
  items: readonly NearbyQuest[],
  selectedId: string | null,
  listAdPreview: boolean,
  viewer: NearbyViewerAt | null,
): void {
  const parts: string[] = [];
  items.forEach((item, index) => {
    pushListCard(
      parts,
      index,
      nearbyItemHtml(item, selectedId === item.id, questListMetaLine(item, viewer)),
      listAdPreview,
    );
  });
  root.innerHTML = parts.join("");
  observeRouteThumbnails(root);
}

export function renderNearbyList(
  root: HTMLElement,
  items: readonly NearbyQuest[],
  selectedId: string | null,
  signedIn: boolean,
  listAdPreview = false,
  viewer: NearbyViewerAt | null = null,
): void {
  if (!items.length) {
    const create = signedIn
      ? `<p class="empty">${escapeHtml(t("nearby.emptyCreate"))}</p>
        <a class="btn btn-primary nearby-new-quest" id="nearby-new-quest" href="/quest/new">${escapeHtml(t("quests.new"))}</a>`
      : "";
    root.innerHTML = `<div class="nearby-empty"><p class="empty">${escapeHtml(t("nearby.empty"))}</p>${create}</div>`;
    return;
  }
  renderCards(root, items, selectedId, listAdPreview, viewer);
}

export function renderSavedList(
  root: HTMLElement,
  items: readonly NearbyQuest[],
  selectedId: string | null,
  signedIn: boolean,
  listAdPreview = false,
  viewer: NearbyViewerAt | null = null,
): void {
  if (!signedIn) {
    root.innerHTML = `<p class="empty">${escapeHtml(t("quests.interestSignIn"))}</p>`;
    return;
  }
  if (!items.length) {
    root.innerHTML = `<div class="nearby-empty"><p class="empty">${escapeHtml(t("quests.interestEmpty"))}</p></div>`;
    return;
  }
  renderCards(root, items, selectedId, listAdPreview, viewer);
}

type MyQuestRow = Pick<QuestDetail, "id" | "status" | "title" | "created_at" | "center_lat" | "center_lng">;

/**
 * My quests rows (drafts and published, newest first). Rows with a route preview get a card
 * with a thumbnail and zoom button; they come back as list quests for light card opens.
 */
export function renderMyQuestList(
  root: HTMLElement,
  missions: readonly MyQuestRow[],
  selectedId: string | null,
  listAdPreview = false,
): NearbyQuest[] {
  const listed: NearbyQuest[] = [];
  const parts: string[] = [];
  let cardIndex = 0;
  for (const m of missions) {
    const draft = m.status === "draft";
    const status = draft ? "draft" : "active";
    const spotFields = m as QuestDetail & Partial<NearbyQuest>;
    const preview = spotFields.route_preview;
    const current = selectedId === m.id;
    const meta = `<span class="quest-item-meta"><span class="quest-status-badge" data-status="${status}">${escapeHtml(t(draft ? "quests.draft" : "quests.published"))}</span><span>${escapeHtml(m.created_at.slice(0, 10))}</span></span>`;
    const cardItem = {
      id: m.id,
      title: questTitle(m),
      quest_type: spotFields.quest_type,
      route_preview: preview,
      spots_preview: spotFields.spots_preview,
      spot_count: spotFields.spot_count,
    };
    const spotThumb = listThumbnailUsesSpotMarkers(spotFields);
    if (spotThumb || isSpotOnlyListQuest(spotFields)) {
      listed.push({
        ...(m as QuestDetail),
        title: questTitle(m),
        start: spotFields.start ?? { lat: m.center_lat, lng: m.center_lng },
        distance_m: spotFields.distance_m ?? 0,
        spots_preview: spotFields.spots_preview,
        spot_count: spotFields.spot_count,
        est_minutes_total: spotFields.est_minutes_total,
        route_preview: spotFields.route_preview,
      } as NearbyQuest);
    } else if (preview && preview.coordinates.length >= 2) {
      listed.push({
        ...(m as QuestDetail),
        start: spotFields.start ?? { lat: m.center_lat, lng: m.center_lng },
        distance_m: spotFields.distance_m ?? 0,
        route_preview: preview,
      } as NearbyQuest);
    } else {
      listed.push({
        ...(m as QuestDetail),
        title: questTitle(m),
        start: spotFields.start ?? { lat: m.center_lat, lng: m.center_lng },
        distance_m: spotFields.distance_m ?? 0,
      } as NearbyQuest);
    }
    const card = nearbyItemHtml(cardItem, current, meta, {
      className: "piece-item",
      attrs: `data-id="${escapeHtml(m.id)}" data-quest-id="${escapeHtml(m.id)}" data-status="${status}"`,
      metaHtml: true,
    });
    pushListCard(parts, cardIndex, `<div class="piece-item-row">${card}</div>`, listAdPreview);
    cardIndex += 1;
  }
  root.innerHTML = parts.join("");
  observeRouteThumbnails(root);
  return listed;
}

/** Mark the selected card in an already-rendered list without re-rendering (the map never moves). */
export function syncListCurrent(root: HTMLElement, selector: string, idOf: (el: HTMLElement) => string | undefined, selectedId: string | null): void {
  for (const item of root.querySelectorAll<HTMLElement>(selector)) {
    item.setAttribute("aria-current", String(selectedId === idOf(item)));
  }
}

export interface QuestListClick {
  id: string;
  /** The card's zoom button: fit the route instead of a light preview. */
  fit: boolean;
  /** My quests draft row (opens through the signed-in missions API). */
  draft: boolean;
}

/** The quest a click in a home list targets, or null for a click between cards. */
export function questListClick(target: Element): QuestListClick | null {
  const zoom = target.closest<HTMLElement>("[data-quest-zoom]");
  const zoomId = zoom?.dataset.questZoom;
  if (zoomId) {
    const draft = target.closest<HTMLElement>("[data-status]")?.dataset.status === "draft";
    return { id: zoomId, fit: true, draft };
  }
  const item = target.closest<HTMLButtonElement>(CARD_SELECTOR);
  const id = item?.dataset.questId ?? item?.dataset.id;
  if (!item || !id) return null;
  return { id, fit: false, draft: item.dataset.status === "draft" };
}

/** Route clicks on a home list to `open`. The zoom button never bubbles into a card click. */
export function bindQuestListClicks(root: HTMLElement, open: (click: QuestListClick, event: Event) => void): void {
  root.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const click = questListClick(target);
    if (!click) return;
    if (click.fit) {
      event.preventDefault();
      event.stopPropagation();
    }
    open(click, event);
  });
}

/** Home tabs: Nearby, My quests, and Saved quests. Returns `show(tab, refresh)`; the stored tab paints at once. */
export function bindHomeTabs(
  tabs: readonly HTMLButtonElement[],
  panels: readonly HTMLElement[],
  onRefresh: (tab: string) => void,
): (tab: string, refresh?: boolean) => void {
  const show = (tab: string, refresh = true): void => {
    const pick = tabs.some((b) => b.dataset.tab === tab) ? tab : "nearby";
    for (const b of tabs) b.setAttribute("aria-selected", String(b.dataset.tab === pick));
    for (const p of panels) p.hidden = p.dataset.tab !== pick;
    try {
      localStorage.setItem(HOME_TAB_KEY, pick);
    } catch {
      /* storage unavailable */
    }
    if (refresh) onRefresh(pick);
  };
  for (const b of tabs) b.addEventListener("click", () => show(b.dataset.tab ?? "nearby"));
  let stored = "nearby";
  try {
    stored = localStorage.getItem(HOME_TAB_KEY) ?? "nearby";
  } catch {
    /* storage unavailable */
  }
  show(stored, false);
  return show;
}

setRouteThumbnailLookup((id) => thumbSources.get(id) ?? null);
